import { useEffect, useState } from 'react';

import { Button, Switch } from '@domcrm/embed-ui';

import { getTelegramLink, patchMeProfile, postTelegramLink, type TelegramLinkResponse } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';

const PLATFORM_TELEGRAM_DISABLED_HINT =
  'Привязка Telegram сейчас недоступна: администратор платформы ещё не включил эту возможность.';

type TelegramLinkCardProps = {
  accessToken: string;
  branchId?: number | null;
  telegramId?: string | null;
  notifyViaTelegram?: boolean | null;
  onReload?: () => void | Promise<void>;
  title?: string;
};

export function TelegramLinkCard({
  accessToken,
  branchId,
  telegramId,
  notifyViaTelegram,
  onReload,
  title = 'Telegram'
}: TelegramLinkCardProps) {
  const [link, setLink] = useState<TelegramLinkResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingNotify, setSavingNotify] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getTelegramLink(accessToken).then(
      (data) => {
        if (!cancelled) setLink(data);
      },
      () => {
        if (!cancelled) setLink(null);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [accessToken, telegramId]);

  useEffect(() => {
    if (!link?.pending || link.linked) return;
    const timer = window.setInterval(() => {
      void getTelegramLink(accessToken).then(setLink).catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [accessToken, link?.linked, link?.pending]);

  const platformTelegramOk = link?.telegram_configured !== false;

  const currentTelegramId = (link?.telegram_id || telegramId || '').trim();
  const telegramEnabled = Boolean(currentTelegramId) && notifyViaTelegram !== false;
  const pendingLink = Boolean(link?.pending) && !currentTelegramId;
  const awaitingTelegramBinding = !currentTelegramId && (pendingLink || notifyViaTelegram === true);
  const pendingBotUsername = link?.bot_username?.trim() || '';
  const actionLabel = currentTelegramId
    ? 'Перепривязать Telegram'
    : pendingLink
      ? 'Продолжить привязку Telegram'
      : notifyViaTelegram === true
        ? 'Завершить привязку Telegram'
        : 'Привязать Telegram';

  async function tryFetchExistingLink(): Promise<TelegramLinkResponse | null> {
    try {
      return await getTelegramLink(accessToken);
    } catch {
      return null;
    }
  }

  async function startLink(): Promise<void> {
    if (link?.telegram_configured === false) {
      setError(PLATFORM_TELEGRAM_DISABLED_HINT);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let data: TelegramLinkResponse;
      try {
        data = await postTelegramLink({ branch_id: branchId ?? undefined }, accessToken);
      } catch (e) {
        if (!(e instanceof ApiError) || e.status < 500) {
          throw e;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 700));
        const existing = await tryFetchExistingLink();
        if (existing?.pending || existing?.linked) {
          data = existing;
        } else {
          data = await postTelegramLink({ branch_id: branchId ?? undefined }, accessToken);
        }
      }
      setLink(data);
      if (data.connect_url) {
        window.open(data.connect_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status >= 500
            ? 'Сервис Telegram сейчас ответил нестабильно. Попробуйте ещё раз через несколько секунд.'
            : e.detail
          : e instanceof Error
            ? e.message
            : 'Не удалось открыть Telegram';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function toggleNotifications(nextValue: boolean): Promise<void> {
    if (!platformTelegramOk) {
      setError(PLATFORM_TELEGRAM_DISABLED_HINT);
      return;
    }
    if (nextValue && !currentTelegramId) {
      await startLink();
      return;
    }
    setSavingNotify(true);
    setError(null);
    try {
      await patchMeProfile({ notify_via_telegram: nextValue }, accessToken);
      await onReload?.();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сохранить настройки';
      setError(msg);
    } finally {
      setSavingNotify(false);
    }
  }

  async function unlinkTelegram(): Promise<void> {
    setSavingNotify(true);
    setError(null);
    try {
      await patchMeProfile({ telegram_id: null, notify_via_telegram: false }, accessToken);
      setLink((prev) =>
        prev
          ? {
              ...prev,
              linked: false,
              pending: false,
              telegram_id: null
            }
          : null
      );
      await onReload?.();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось отвязать Telegram';
      setError(msg);
    } finally {
      setSavingNotify(false);
    }
  }

  return (
    <section className="rounded-sm border border-[color-mix(in_srgb,var(--border)_70%,transparent)] bg-[var(--bg-soft)] p-4 text-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-[13px] font-medium text-[var(--text)]">{title}</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--text-muted)]">
            {!platformTelegramOk ? (
              PLATFORM_TELEGRAM_DISABLED_HINT
            ) : currentTelegramId ? (
              `Привязан chat ID ${currentTelegramId}.`
            ) : pendingLink ? (
              `Откройте Telegram${pendingBotUsername ? ` и напишите /start боту @${pendingBotUsername}` : ''}.`
            ) : awaitingTelegramBinding ? (
              'Уведомления включены, но Telegram ещё не привязан. Откройте чат с ботом и завершите привязку.'
            ) : (
              'Подключите Telegram, чтобы получать уведомления без ручного ввода chat ID.'
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[13px] text-[var(--text)]">
          <span id="telegram-notify-label">Уведомления</span>
          <Switch
            checked={platformTelegramOk ? telegramEnabled : false}
            disabled={busy || savingNotify || !platformTelegramOk}
            onCheckedChange={(v) => void toggleNotifications(v)}
            aria-labelledby="telegram-notify-label"
            aria-label="Включить уведомления в Telegram"
          />
        </div>
      </div>

      {platformTelegramOk ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" disabled={busy || savingNotify} onClick={() => void startLink()}>
            {actionLabel}
          </Button>
          {link?.connect_url ? (
            <Button
              type="button"
              variant="ghost"
              disabled={busy || savingNotify}
              onClick={() => window.open(link.connect_url ?? '', '_blank', 'noopener,noreferrer')}
            >
              Открыть чат
            </Button>
          ) : null}
          {currentTelegramId ? (
            <Button type="button" variant="ghost" disabled={busy || savingNotify} onClick={() => void unlinkTelegram()}>
              Отвязать
            </Button>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-xs text-[var(--error-text)]">{error}</p> : null}
    </section>
  );
}
